import { useCallback } from 'react'
import type { LinkStatus, EndpointDisplayInfo } from '../../types/infrastructure-map'
import type { EndpointRelationship } from '../../types/case-container/relationships'
import type { LinkContextMenuState, NodeContextMenuState } from './canvas'
import { logger } from '../../utils/logger'

interface UseCanvasHandlersArgs {
  relationships: EndpointRelationship[]
  selectedEndpointIds: Set<string>
  setSelectedEndpointIds: React.Dispatch<React.SetStateAction<Set<string>>>
  setNodeContextMenu: React.Dispatch<React.SetStateAction<NodeContextMenuState | null>>
  setLinkContextMenu: React.Dispatch<React.SetStateAction<LinkContextMenuState | null>>
  setEditLinkId: React.Dispatch<React.SetStateAction<string | null>>
  linkContextMenu: LinkContextMenuState | null
  isInSet: (id: string) => boolean
  displayInfo: Map<string, EndpointDisplayInfo>
  linkCreation: {
    state: { parentEndpointId: string | null; childEndpointIds: string[] }
    reset: () => void
  }
  mapSet: { addMany: (ids: string[]) => void }
}

export interface CanvasHandlers {
  handleNodeContextMenu: (endpointId: string, e: React.MouseEvent) => void
  handleCanvasClick: () => void
  handleLinkContextMenu: (relationshipId: string, e: React.MouseEvent) => void
  handleViewLinkDetails: () => void
  handleCloseLinkModal: () => void
  handleLinkComplete: (linkType: LinkStatus, openQuestionText?: string) => void
  handleAddToMap: (endpointId: string) => void
}

interface NodeContextMenuHandlerArgs {
  displayInfo: Map<string, EndpointDisplayInfo>
  selectedEndpointIds: Set<string>
  isInSet: (id: string) => boolean
  setSelectedEndpointIds: React.Dispatch<React.SetStateAction<Set<string>>>
  setNodeContextMenu: React.Dispatch<React.SetStateAction<NodeContextMenuState | null>>
}

function useNodeContextMenuHandler({
  displayInfo,
  selectedEndpointIds,
  isInSet,
  setSelectedEndpointIds,
  setNodeContextMenu,
}: NodeContextMenuHandlerArgs): (endpointId: string, e: React.MouseEvent) => void {
  return useCallback(
    (endpointId: string, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const info = displayInfo.get(endpointId)
      const isAlreadySelected = selectedEndpointIds.has(endpointId)
      if (!isAlreadySelected) setSelectedEndpointIds(new Set([endpointId]))
      setNodeContextMenu({
        x: e.clientX,
        y: e.clientY,
        endpointId,
        currentSize: info?.tileSize ?? 'small',
        isInMapSet: info?.isInMapSet ?? false,
        isInCollectionSet: isInSet(endpointId),
        selectedCount: isAlreadySelected ? selectedEndpointIds.size : 1,
      })
    },
    [displayInfo, selectedEndpointIds, isInSet, setSelectedEndpointIds, setNodeContextMenu]
  )
}

interface LinkActionHandlersArgs {
  linkContextMenu: LinkContextMenuState | null
  setLinkContextMenu: React.Dispatch<React.SetStateAction<LinkContextMenuState | null>>
  setEditLinkId: React.Dispatch<React.SetStateAction<string | null>>
  linkCreation: {
    state: { parentEndpointId: string | null; childEndpointIds: string[] }
    reset: () => void
  }
}

function useLinkActionHandlers({
  linkContextMenu,
  setLinkContextMenu,
  setEditLinkId,
  linkCreation,
}: LinkActionHandlersArgs): Pick<
  CanvasHandlers,
  'handleViewLinkDetails' | 'handleCloseLinkModal' | 'handleLinkComplete'
> {
  const handleViewLinkDetails = useCallback(() => {
    if (!linkContextMenu) return
    setEditLinkId(linkContextMenu.relationshipId)
    setLinkContextMenu(null)
  }, [linkContextMenu, setEditLinkId, setLinkContextMenu])

  const handleCloseLinkModal = useCallback(() => {
    setEditLinkId(null)
  }, [setEditLinkId])

  const handleLinkComplete = useCallback(
    (linkType: LinkStatus, openQuestionText?: string) => {
      logger.debug('InfrastructureMapCanvas', 'Creating link', {
        parent: linkCreation.state.parentEndpointId,
        children: linkCreation.state.childEndpointIds,
        type: linkType,
        text: openQuestionText,
      })
      linkCreation.reset()
    },
    [linkCreation]
  )

  return { handleViewLinkDetails, handleCloseLinkModal, handleLinkComplete }
}

export function useCanvasHandlers({
  relationships,
  selectedEndpointIds,
  setSelectedEndpointIds,
  setNodeContextMenu,
  setLinkContextMenu,
  setEditLinkId,
  linkContextMenu,
  isInSet,
  displayInfo,
  linkCreation,
  mapSet,
}: UseCanvasHandlersArgs): CanvasHandlers {
  const handleNodeContextMenu = useNodeContextMenuHandler({
    displayInfo,
    selectedEndpointIds,
    isInSet,
    setSelectedEndpointIds,
    setNodeContextMenu,
  })

  const handleCanvasClick = useCallback(() => {
    setNodeContextMenu(null)
    setLinkContextMenu(null)
  }, [setNodeContextMenu, setLinkContextMenu])

  const handleLinkContextMenu = useCallback(
    (relationshipId: string, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const rel = relationships.find((relItem) => relItem.id === relationshipId)
      if (!rel) return
      const menuData: LinkContextMenuState = {
        x: e.clientX,
        y: e.clientY,
        relationshipId,
        status: (rel.status as LinkStatus | undefined) ?? 'confirmed',
        hasOpenQuestion: rel.status === 'open-question',
      }
      if (rel.investigationNotes) menuData.openQuestionText = rel.investigationNotes
      setLinkContextMenu(menuData)
    },
    [relationships, setLinkContextMenu]
  )

  const linkActions = useLinkActionHandlers({
    linkContextMenu,
    setLinkContextMenu,
    setEditLinkId,
    linkCreation,
  })

  const handleAddToMap = useCallback(
    (endpointId: string) => {
      mapSet.addMany([endpointId])
    },
    [mapSet]
  )

  return {
    handleNodeContextMenu,
    handleCanvasClick,
    handleLinkContextMenu,
    ...linkActions,
    handleAddToMap,
  }
}
